import { FirebaseApp, getApps, initializeApp } from "firebase/app";
import type { MessagePayload } from "firebase/messaging";
import {
  Messaging,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from "firebase/messaging";

const FCM_LAST_REGISTERED_TOKEN_KEY = "fcm_last_registered_token";

export interface FirebaseWebConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
}

export function getFirebaseWebConfigFromEnv(): FirebaseWebConfig | null {
  const apiKey = process.env.REACT_APP_FCM_API_KEY || "";
  const authDomain = process.env.REACT_APP_FCM_AUTH_DOMAIN || "";
  const projectId = process.env.REACT_APP_FCM_PROJECT_ID || "";
  const storageBucket = process.env.REACT_APP_FCM_STORAGE_BUCKET || "";
  const messagingSenderId = process.env.REACT_APP_FCM_MESSAGING_SENDER_ID || "";
  const appId = process.env.REACT_APP_FCM_APP_ID || "";
  if (!apiKey || !projectId || !appId) {
    return null;
  }
  return {
    apiKey,
    authDomain,
    projectId,
    storageBucket,
    messagingSenderId,
    appId,
  };
}

let appInstance: FirebaseApp | null = null;

export function getFirebaseApp(): FirebaseApp | null {
  const config = getFirebaseWebConfigFromEnv();
  if (!config) {
    return null;
  }
  if (!appInstance) {
    appInstance = getApps().length > 0 ? getApps()[0]! : initializeApp(config);
  }
  return appInstance;
}

export async function getFirebaseMessaging(): Promise<Messaging | null> {
  const supported = await isSupported();
  if (!supported) {
    return null;
  }
  const app = getFirebaseApp();
  if (!app) {
    return null;
  }
  return getMessaging(app);
}

export async function isFcmBrowserSupported(): Promise<boolean> {
  if (!("Notification" in window) || !("serviceWorker" in navigator)) {
    return false;
  }
  return isSupported();
}

export async function registerFirebaseMessagingServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) {
    return null;
  }
  try {
    const config = getFirebaseWebConfigFromEnv();
    if (!config) {
      return null;
    }
    const params = new URLSearchParams({
      apiKey: config.apiKey,
      authDomain: config.authDomain,
      projectId: config.projectId,
      storageBucket: config.storageBucket,
      messagingSenderId: config.messagingSenderId,
      appId: config.appId,
    });
    return await navigator.serviceWorker.register(
      `/firebase-messaging-sw.js?${params.toString()}`,
    );
  } catch (e) {
    console.error("FCM service worker registration failed", e);
    return null;
  }
}

export async function requestFcmToken(): Promise<string | null> {
  const vapidKey = process.env.REACT_APP_FCM_VAPID_KEY || "";
  const messaging = await getFirebaseMessaging();
  if (!messaging) {
    return null;
  }
  const registration = await registerFirebaseMessagingServiceWorker();
  const token = await getToken(messaging, {
    vapidKey: vapidKey || undefined,
    serviceWorkerRegistration: registration || undefined,
  });
  return token || null;
}

export type FcmForegroundHandler = (payload: MessagePayload) => void;

export async function subscribeForegroundMessages(
  handler: FcmForegroundHandler,
): Promise<() => void> {
  const messaging = await getFirebaseMessaging();
  if (!messaging) {
    return () => {};
  }
  return onMessage(messaging, handler);
}

export interface RegisterFcmTokenPayload {
  token: string;
}

export interface BellNotificationItem {
  title: string;
  body?: string;
  payload?: string;
  key: string;
  createdAt: string;
}

interface BackendNotificationData {
  title?: string;
  message?: string;
  data?: string;
  payload?: {
    download_url?: string;
    file_name?: string;
    status?: string;
  };
}

interface BackendNotificationItem {
  id: string;
  data?: BackendNotificationData;
  created_at?: string;
}

interface BackendNotificationListResponse {
  data?: BackendNotificationItem[];
}

/**
 * POST JSON payload expected by /api/device-tokens:
 * { device_type, device_id, token, token_type }
 */
export async function registerFcmTokenWithBackend(
  payload: RegisterFcmTokenPayload,
): Promise<Response | null> {
  const lastRegisteredToken = localStorage.getItem(FCM_LAST_REGISTERED_TOKEN_KEY);
  if (lastRegisteredToken && lastRegisteredToken === payload.token) {
    return null;
  }
  const apiUrl = process.env.REACT_APP_API_URL || "";
  if (!apiUrl) {
    return null;
  }
  const url = `${apiUrl.replace(/\/+$/, "")}/device-tokens`;
  const authToken = localStorage.getItem("token");
  const storedDeviceId = localStorage.getItem("fcm_device_id");
  const deviceId =
    storedDeviceId || `web-${Math.random().toString(36).slice(2, 12)}`;
  if (!storedDeviceId) {
    localStorage.setItem("fcm_device_id", deviceId);
  }
  const body = {
    device_type: "web",
    device_id: deviceId,
    token: payload.token,
    token_type: "fcm",
  };
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (response.ok) {
    localStorage.setItem(FCM_LAST_REGISTERED_TOKEN_KEY, payload.token);
  }
  return response;
}

export async function fetchBellNotifications(
  limit = 20,
): Promise<BellNotificationItem[]> {
  const apiUrl = process.env.REACT_APP_API_URL || "";
  if (!apiUrl) {
    return [];
  }
  const authToken = localStorage.getItem("token");
  if (!authToken) {
    return [];
  }
  const normalizedApiUrl = apiUrl.replace(/\/+$/, "");
  const url = `${normalizedApiUrl}/inboxes/export-import/notifications?page=1&limit=${limit}`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${authToken}`,
    },
  });
  if (!response.ok) {
    throw new Error(`Notification fetch failed with status ${response.status}`);
  }
  const json = (await response.json()) as BackendNotificationListResponse;
  const items = Array.isArray(json.data) ? json.data : [];

  return items.map((item) => {
    const payloadObj = item.data?.payload;
    return {
      title: item.data?.title || "Notification",
      body: item.data?.message || item.data?.data,
      payload: payloadObj ? JSON.stringify(payloadObj) : undefined,
      key: item.id,
      createdAt: item.created_at || new Date().toISOString(),
    };
  });
}
